import { Download, FileCheck2, Loader2, ScrollText } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { EmptyRunState } from "../../components/EmptyRunState";
import { Button } from "../../components/primitives";
import { useReviewMachine } from "../../hooks/useReviewMachine";
import { editRemoteRun, fetchRunDocument } from "../../lib/api";
import { apiUrl } from "../../lib/env";
import {
  advisoriesForBlock,
  deriveGates,
  gateCounts,
  protectedFactViolations,
  protectedFactsClear,
  runLevelAdvisories,
  type Advisory
} from "../../lib/qa";
import type { ChangeItem, RunStatus } from "../../lib/types";
import { AskAtlasComposer } from "./AskAtlasComposer";
import { DocumentFrame } from "./DocumentFrame";
import { ExportConfirm } from "./ExportConfirm";
import { GateChecklist } from "./GateChecklist";
import { OverrideAttestation } from "./OverrideAttestation";
import { PhaseRail, type StudioPhase } from "./PhaseRail";
import { ProtectedFactBanner } from "./ProtectedFactBanner";
import { QaResultPanel } from "./QaResultPanel";
import { ReadinessStrip } from "./ReadinessStrip";
import { RunTray } from "./RunTray";
import { SuggestionCard } from "./SuggestionCard";

/** The element the compiler edits, anchored by the id the backend tags on the document. */
function anchorOf(change: ChangeItem) {
  return change.blockId || change.id;
}

type DocState = "loading" | "ready" | "error" | "unavailable";

/**
 * STUDIO — the whole product on one screen.
 *
 * Slice 1: the real document is the hero, compiler suggestions are inline editorial comments.
 * Slice 2: after Approve, QA returns as advisory comments on the SAME document — block-anchored
 * notes, a run-level tray, and a demoted six-check strip — with exactly one hard stop, the
 * protected-fact banner, which is the only thing that disables export.
 */
export function StudioPage({
  runs,
  onRun,
  setToast
}: {
  runs: RunStatus[];
  onRun: (run: RunStatus) => void;
  setToast: (message: string) => void;
}) {
  const { runId } = useParams();
  const run = useMemo(() => runs.find((item) => item.runId === runId), [runId, runs]);
  const machine = useReviewMachine(run, onRun, setToast);

  const [docHtml, setDocHtml] = useState<string | null>(null);
  const [docState, setDocState] = useState<DocState>("loading");
  const [focusedBlockId, setFocusedBlockId] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [attestFor, setAttestFor] = useState<Advisory | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [delivered, setDelivered] = useState(false);
  const [composerBusy, setComposerBusy] = useState(false);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const activeRunId = run?.runId;

  useEffect(() => {
    if (!activeRunId) return;
    if (!apiUrl) {
      setDocState("unavailable");
      return;
    }
    let cancelled = false;
    setDocState("loading");
    setDocHtml(null);
    fetchRunDocument(activeRunId)
      .then((html) => {
        if (cancelled) return;
        setDocHtml(html);
        setDocState("ready");
      })
      .catch(() => {
        if (!cancelled) setDocState("error");
      });
    return () => {
      cancelled = true;
    };
  }, [activeRunId]);

  useEffect(() => {
    if (!focusedBlockId) return;
    cardRefs.current[focusedBlockId]?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [focusedBlockId]);

  // Keyboard: j / ArrowDown and k / ArrowUp walk the suggestions in document order. Ignored while
  // typing in a field or with a modal open, so it never fights the attestation note or an input.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (!run || attestFor || exportOpen) return;
      const tag = (event.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      const isNext = event.key === "j" || event.key === "ArrowDown";
      const isPrev = event.key === "k" || event.key === "ArrowUp";
      if (!isNext && !isPrev) return;
      const ids = run.changes.map((change) => change.blockId || change.id);
      if (!ids.length) return;
      event.preventDefault();
      setFocusedBlockId((current) => {
        const index = current ? ids.indexOf(current) : -1;
        if (index === -1) return ids[0];
        const next = isNext ? Math.min(ids.length - 1, index + 1) : Math.max(0, index - 1);
        return ids[next];
      });
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [run, attestFor, exportOpen]);

  if (!run || !machine.view) {
    return <EmptyRunState stage="review" />;
  }

  const { view, setVerdict, approveRun, qaState } = machine;
  const changes = run.changes;
  const total = changes.length;
  const resolved = view.decidedCount;

  const suggestedBlockIds = changes.map(anchorOf);
  const appliedTexts: Record<string, string | null> = {};
  for (const change of changes) {
    appliedTexts[anchorOf(change)] = change.verdict === "approved" ? change.after : null;
  }

  // --- Advisory QA (present only once QA has run) -------------------------------------------
  const qaRan = Boolean(run.qaLoop);
  const gates = deriveGates(run, qaState);
  const counts = gateCounts(gates);
  const violations = protectedFactViolations(gates);
  const factsClear = protectedFactsClear(gates);
  const exportClear = qaRan && factsClear;
  const runAdvisories = runLevelAdvisories(gates).filter((advisory) => !dismissed.has(advisory.message));
  // The FULL review panel — all five lenses, passes included. Passing critique is honest reasoning
  // the operator asked to see; hiding it left the "About this run" tray looking empty on a clean run.
  const reviewPanel = run.qaLoop?.reviewPanel || [];
  const reviewSummary = run.qaLoop?.reviewSummary;
  const openAdvisoryCount = changes.reduce(
    (sum, change) => sum + advisoriesForBlock(gates, anchorOf(change)).filter((a) => !dismissed.has(a.message)).length,
    runAdvisories.length
  );

  // ONE phase drives the screen — the honest replacement for the retired Draft->Review->Package flow.
  const phase: StudioPhase = delivered
    ? "done"
    : view.qaRunning
      ? "qa-running"
      : view.qaBlocked || (qaRan && !factsClear)
        ? "blocked"
        : view.qaPassed || view.recordedLocally
          ? "ready"
          : "draft";

  const focusChange = (change: ChangeItem) => setFocusedBlockId(anchorOf(change));
  // A rail decision (Accept / Keep) must feel like it moved the queue: record the verdict, then
  // collapse this card by advancing focus to the next still-open suggestion in document order (or
  // clear focus when none remain, which surfaces the "Approve & run final QA" next step).
  const decideAndAdvance = (change: ChangeItem, verdict: "approved" | "rejected") => {
    void setVerdict(change.id, verdict);
    const decidedAnchor = anchorOf(change);
    const decidedIndex = suggestedBlockIds.indexOf(decidedAnchor);
    const isOpen = (item: ChangeItem) =>
      item.id !== change.id && item.verdict !== "approved" && item.verdict !== "rejected";
    const nextOpen =
      changes.find((item) => isOpen(item) && suggestedBlockIds.indexOf(anchorOf(item)) > decidedIndex) ||
      changes.find(isOpen) ||
      null;
    setFocusedBlockId(nextOpen ? anchorOf(nextOpen) : null);
  };
  const onBlockFocus = (blockId: string) => {
    if (changes.some((change) => anchorOf(change) === blockId)) setFocusedBlockId(blockId);
  };
  // Direct edit: the operator typed into a block. It lands as that block's change, authored and
  // approved by them, and rides the same review->QA path (which clears any prior QA). Full layout +
  // protected-fact validation still runs at Approve; nothing bypasses the gate.
  const onBlockEdit = (blockId: string, text: string) => {
    const change = changes.find((item) => anchorOf(item) === blockId);
    if (!change) return;
    setToast("Your edit saved");
    void setVerdict(change.id, "approved", { after: text, reason: "Edited directly by the operator." });
  };
  // Ask ATLAS: a targeted instruction on the focused block. It returns a PREVIEWED pending change
  // into the same review flow; QA is cleared because the change set changed. Never auto-applied.
  const focusedChange = changes.find((item) => anchorOf(item) === focusedBlockId) || null;
  const applyInstruction = async (prompt: string, research: boolean) => {
    if (!focusedChange || !apiUrl) return;
    setComposerBusy(true);
    setToast(research ? "ATLAS is researching and drafting…" : "ATLAS is drafting…");
    try {
      const updated = await editRemoteRun(run.runId, prompt, run.document, {
        target: { blockId: anchorOf(focusedChange) },
        research
      });
      onRun({ ...updated, qaLoop: undefined, validation: undefined, finalCopy: undefined, status: "needs_review" });
      setToast("ATLAS proposed an edit — review it below");
    } catch {
      setToast("Ask ATLAS could not reach the backend");
    } finally {
      setComposerBusy(false);
    }
  };
  const recordOverride = (advisory: Advisory, reason: string, note: string) => {
    onRun({
      ...run,
      overrides: [
        ...(run.overrides || []),
        {
          finding: advisory.message,
          blockId: advisory.blockId,
          gateId: advisory.gateId,
          reason,
          note: note.trim() || undefined,
          at: new Date().toISOString()
        }
      ]
    });
    setDismissed((current) => new Set(current).add(advisory.message));
    setAttestFor(null);
    setToast("Override recorded");
  };

  const document = (
    <main className="studio-doc" data-testid="studio-doc">
      {docState === "ready" && docHtml ? (
        <DocumentFrame
          html={docHtml}
          suggestedBlockIds={suggestedBlockIds}
          editableBlockIds={suggestedBlockIds}
          focusedBlockId={focusedBlockId}
          appliedTexts={appliedTexts}
          onBlockFocus={onBlockFocus}
          onBlockEdit={onBlockEdit}
          onReady={() => undefined}
        />
      ) : (
        <div className="studio-doc-status" role="status" data-state={docState}>
          {docState === "loading" ? (
            <>
              <Loader2 className="spin" aria-hidden="true" />
              <p>Loading the document…</p>
            </>
          ) : docState === "unavailable" ? (
            <>
              <ScrollText aria-hidden="true" />
              <p>The full document is served by the backend. This is a local preview, so only the suggestions are shown.</p>
            </>
          ) : (
            <>
              <ScrollText aria-hidden="true" />
              <p>The document could not be loaded. The suggestions still apply to this run.</p>
            </>
          )}
        </div>
      )}
    </main>
  );

  const primaryAction = exportClear ? (
    <Button variant="primary" data-testid="studio-export" onClick={() => setExportOpen(true)}>
      <Download aria-hidden="true" />
      {openAdvisoryCount ? `Export document (${openAdvisoryCount} advisory)` : "Export document"}
    </Button>
  ) : (
    <Button
      variant="primary"
      data-testid="studio-approve"
      busy={view.qaRunning}
      disabled={view.qaButtonDisabled || violations.length > 0}
      reason={violations.length ? "Fix the protected-fact issue to export." : view.qaButtonReason || undefined}
      onClick={() => void approveRun(view.pendingCount)}
    >
      {view.qaRunning ? null : <FileCheck2 aria-hidden="true" />}
      {view.pendingCount ? "Resolve all to approve" : qaRan ? "Re-run final QA" : "Approve & run final QA"}
    </Button>
  );

  return (
    <div className="studio" data-testid="studio-page">
      <header className="studio-head">
        <div className="studio-head-main">
          <p className="studio-eyebrow">{run.input.organization || "This run"}</p>
          <h1 className="studio-title">{run.document.title || "Audience document"}</h1>
        </div>
        <div className="studio-head-meta">
          <p className="studio-counter" data-testid="studio-counter" role="status" aria-live="polite">
            <strong>{resolved}</strong> of {total} suggestion{total === 1 ? "" : "s"} resolved
          </p>
          {primaryAction}
        </div>
      </header>

      <PhaseRail phase={phase} />

      {view.qaRunning ? (
        <p className="studio-qa-strip" data-state="running" role="status">
          {view.qaActionMessage}
        </p>
      ) : null}

      {qaRan && !view.qaRunning ? (
        <>
          <QaResultPanel
            headline={
              !factsClear
                ? "Protected-fact issue — must fix to export"
                : openAdvisoryCount
                  ? `Ready to export — ${openAdvisoryCount} advisory suggestion${openAdvisoryCount === 1 ? "" : "s"} to weigh`
                  : "Clean — ready to export"
            }
            subhead={`${counts.passed} of ${counts.total} checks passed`}
            checksRan={counts.ran}
            checksTotal={counts.total}
            passed={counts.passed}
            advisories={openAdvisoryCount}
            reviewers={reviewPanel.length}
            state={!factsClear ? "blocked" : view.recordedLocally ? "recorded" : "passed"}
          />
          <ReadinessStrip
            protectedFactsClear={factsClear}
            checksPassed={counts.passed}
            checksTotal={counts.total}
            blocking={violations.length}
            advisoriesOpen={openAdvisoryCount}
            decided={view.decidedCount}
            total={total}
          />
          <GateChecklist gates={gates} />
        </>
      ) : null}

      {violations.length ? <ProtectedFactBanner violations={violations} /> : null}

      <div className="studio-body">
        {document}

        <aside className="studio-rail" data-testid="studio-rail">
          <div className="studio-rail-head">
            <h2>Suggestions</h2>
            <p>{total ? "Accept or keep each — or type directly into a highlighted paragraph. The document updates as you go." : "No suggestions — read and export when ready."}</p>
          </div>
          <div className="studio-rail-list">
            {changes.map((change, index) => {
              const anchor = anchorOf(change);
              const blockAdvisories = advisoriesForBlock(gates, anchor).filter((a) => !dismissed.has(a.message));
              return (
                <div
                  key={change.id}
                  ref={(node) => {
                    cardRefs.current[anchor] = node;
                  }}
                >
                  <SuggestionCard
                    change={change}
                    focused={focusedBlockId === anchor}
                    ordinal={String(index + 1).padStart(2, "0")}
                    onFocus={() => focusChange(change)}
                    onAccept={() => decideAndAdvance(change, "approved")}
                    onKeep={() => decideAndAdvance(change, "rejected")}
                  />
                  {blockAdvisories.length ? (
                    <ul className="studio-block-advisories">
                      {blockAdvisories.map((advisory, aIndex) => (
                        <li key={`${advisory.gateId}-${aIndex}`}>
                          <button type="button" className="studio-advisory" onClick={() => setFocusedBlockId(anchor)}>
                            <span className="studio-advisory-chip">Suggestion</span>
                            <span className="studio-advisory-note">{advisory.message}</span>
                          </button>
                          <button type="button" className="studio-advisory-dismiss" onClick={() => setAttestFor(advisory)}>
                            Dismiss
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              );
            })}
          </div>

          {qaRan ? (
            <RunTray summary={reviewSummary} advisories={runAdvisories} panel={reviewPanel} />
          ) : null}

          {apiUrl ? (
            <AskAtlasComposer
              targetLabel={focusedChange ? focusedChange.title : null}
              busy={composerBusy}
              onSubmit={applyInstruction}
            />
          ) : null}
        </aside>
      </div>

      {attestFor ? (
        <OverrideAttestation
          advisory={attestFor}
          onCancel={() => setAttestFor(null)}
          onConfirm={(reason, note) => recordOverride(attestFor, reason, note)}
        />
      ) : null}

      {exportOpen ? (
        <ExportConfirm
          run={run}
          accepted={view.approvedCount}
          kept={view.rejectedCount}
          overridden={run.overrides?.length || 0}
          unaddressed={openAdvisoryCount}
          onCancel={() => setExportOpen(false)}
          onExported={() => {
            setExportOpen(false);
            setDelivered(true);
            setToast("Export package downloading");
          }}
        />
      ) : null}
    </div>
  );
}
